/**
 * Payments the bank has taken back, for one connection.
 *
 * WHY THIS EXISTS AS A SCREEN AT ALL. The daily job reverses anything it
 * can attribute with confidence, so most chargebacks never need a
 * person. What reaches this list is what it would not decide alone —
 * and, until Columbia answers whether an ACH chargeback carries any
 * identifier of ours, that is EVERY ACH chargeback. See
 * `back-end-rs/docs/CHARGEBACK_DESIGN.md` §11 Q1.
 *
 * WHY CONNECTION-SCOPED rather than per-association: a chargeback we
 * could not match has no HOA to be filed under, so it would appear in no
 * association's list. This is the only place it is visible.
 *
 * THE HOLD REASON IS THE POINT OF THE TABLE. `ach_matching_not_supported`
 * means we have no identifier to look with and nobody should go hunting;
 * `unmatched` means we looked and found nothing, which IS worth
 * somebody's time. Those two were briefly collapsed into one useless
 * value by a bug found in a cluster on 2026-09-14 — keeping them apart
 * is the difference between a queue that can be worked and one that
 * cannot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading, Subheading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { Button } from './common/Catalyst/button';
import { Input } from './common/Catalyst/input';
import { Link } from './common/Catalyst/link';
import { bankService } from './clients/ApiClients';
import {
  ChargebackCandidate,
  ChargebackList,
  ChargebackReturn,
} from './clients/rust-api-client';
import { formatDate, getBankErrorParts } from './utils/bankHelpers';
import ChargebackDocumentsDialog from './ChargebackDocumentsDialog';

interface ChargebackPanelProps {
  connectionId: string;
}

/**
 * Why a chargeback has not reversed, in a manager's words.
 *
 * Keyed on the server's `hold_reason` vocabulary verbatim, so the screen
 * and the logs use the same words — somebody reading
 * `ach_matching_not_supported` in a log line finds the same phrase here.
 *
 * An unrecognised reason is shown as-is rather than hidden: a hold
 * nobody can read is still a hold somebody has to clear.
 */
const HOLD_HELP: Record<string, string> = {
  ach_matching_not_supported:
    'We cannot match ACH returns yet — the bank sends no identifier of ours on them. Attach the payment by hand.',
  unmatched:
    'No payment matched this cheque number and amount. It may belong to another association, or the payment may not have posted yet.',
  ambiguous:
    'More than one payment matched, and nothing separated them. Attaching the wrong one would take money off a homeowner who paid.',
  no_check_number:
    'The bank sent no cheque number, which is the only thing that reaches an individual payment.',
  amount_missing: 'The bank sent no amount.',
  amount_unreadable:
    'The amount could not be read. This is a defect worth reporting, not an unusual chargeback.',
  unhandled_type: 'A chargeback type this app does not handle.',
  payment_already_returned:
    'That payment has already been returned — most likely by an earlier chargeback for the same item.',
  payment_not_reversible:
    'The payment was voided, refunded or never applied, so there is nothing to reverse.',
  payment_not_matched: 'The payment is not matched to a home.',
  payment_missing: 'The matched payment no longer exists.',
  retired_tenure:
    'The home changed hands. Which owner wears this is a decision with money on both sides.',
  not_enabled:
    'Automatic posting is switched off for this association. Turning it back on will reverse the backlog.',
  hoa_inactive: 'The association is inactive.',
};

/**
 * The lockbox web portal, where the eKlik Reversal Details report lives
 * — the only place the payer's account number on an ACH return can be
 * read, since the API does not carry it (CHARGEBACK_DESIGN.md §11 Q1).
 *
 * **IP-whitelisted like the API**, so a plain click from a laptop does
 * not reach it. The pointer says so, and names the script that does.
 */
const LOCKBOX_PORTAL_URL = 'https://portal.columbiabank.io/';

const isAch = (c: ChargebackReturn): boolean =>
  (c.chargeback_type ?? '').toUpperCase() === 'ACH';

type Tone = 'lime' | 'amber' | 'zinc' | 'red';

/** What the row's state is called, and how loud it should look. */
const statusInfo = (c: ChargebackReturn): { label: string; tone: Tone } => {
  if (c.action_status === 'reversed') {
    return { label: 'Reversed', tone: 'lime' };
  }
  if (c.action_status === 'ignored') return { label: 'Ignored', tone: 'zinc' };
  if (c.match_status === 'resolved') {
    // Matched but not yet acted on: the next run will reverse it.
    return { label: 'Will reverse', tone: 'amber' };
  }
  return { label: 'Needs a person', tone: 'red' };
};

/**
 * Whether to offer the attach form.
 *
 * **A hint, not the rule** — the server decides, and it can refuse for
 * reasons this cannot see: that the payment came through a different
 * lockbox, or is not matched to a home. Duplicating those checks here
 * would mean two copies of the thing standing between a bounced cheque
 * and a homeowner's ledger, and copies drift.
 */
const canAttach = (c: ChargebackReturn): boolean =>
  c.action_status !== 'reversed' && c.payment_id == null;

/**
 * Whether everything this chargeback can have is in our own storage:
 * both sides and the notice for a cheque, the notice alone for an ACH
 * return. Stored copies outlive the bank's retention; the badge says
 * the evidence is ours now.
 */
const documentsSaved = (c: ChargebackReturn): boolean => {
  const d = c.documents;
  const ach = (c.chargeback_type ?? '').toUpperCase() === 'ACH';
  return ach ? d.notice : d.check_front && d.check_back && d.notice;
};

const ChargebackPanel: React.FC<ChargebackPanelProps> = ({ connectionId }) => {
  const [data, setData] = useState<ChargebackList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState(true);
  const [attaching, setAttaching] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState('');
  const [candidates, setCandidates] = useState<ChargebackCandidate[] | null>(
    null
  );
  const [claimedElsewhere, setClaimedElsewhere] = useState(0);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [showOtherId, setShowOtherId] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<ChargebackReturn | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankService.listConnectionChargebacks({
        connectionId,
        openOnly,
      });
      setData(response.data);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setError(body || "Couldn't load chargebacks.");
    } finally {
      setLoading(false);
    }
  }, [connectionId, openOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Attach a chargeback to the payment it reverses.
   *
   * **This does not reverse anything.** The row goes back to `pending`
   * and the next run applies the same guards it applies to everything
   * the matcher resolved — which is why the button says "Attach" and the
   * badge then says "Will reverse" rather than "Reversed".
   *
   * Reloads the whole list rather than patching the row: an attach can
   * change what a LATER row resolves to, since the payment it names is
   * no longer available to anything else.
   */
  /**
   * Open the attach form for one row and fetch what the server would
   * accept. The list is the server's, computed from the same rule the
   * attach enforces, so what is offered here is never refused there —
   * and a manager picks a home and an amount instead of pasting an id
   * copied out of a PDF, which is where mis-attaches come from.
   */
  const openAttach = useCallback(async (chargebackId: string) => {
    setAttaching(chargebackId);
    setPaymentId('');
    setShowOtherId(false);
    setCandidates(null);
    setCandidatesError(null);
    setAttachError(null);
    try {
      const response = await bankService.listChargebackCandidates({
        chargebackId,
      });
      setCandidates(response.data.candidates);
      setClaimedElsewhere(response.data.claimed_elsewhere);
    } catch (e) {
      const { body } = getBankErrorParts(e);
      setCandidatesError(body || "Couldn't load the payments this could be.");
      setCandidates([]);
    }
  }, []);

  const attach = useCallback(
    async (chargebackId: string, chosen: string) => {
      setBusy(true);
      setAttachError(null);
      try {
        await bankService.resolveChargeback({
          chargebackId,
          chargebackResolveRequest: { payment_id: chosen.trim() },
        });
        setAttaching(null);
        setPaymentId('');
        setCandidates(null);
        await load();
      } catch (e) {
        const { body } = getBankErrorParts(e);
        setAttachError(body || "Couldn't attach that chargeback.");
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  const neverPulled = useMemo(
    () => data != null && data.synced_through == null,
    [data]
  );

  return (
    <div className="mt-8">
      <div className="flex items-end justify-between gap-4">
        <div>
          <Heading level={2}>Returned payments</Heading>
          <Text className="mt-1">
            Payments the bank has taken back. Anything confidently matched
            reverses by itself; what is held below is waiting on a person.
          </Text>
        </div>
        <Button outline onClick={() => setOpenOnly((v) => !v)} disabled={loading}>
          {openOnly ? 'Show all' : 'Show only open'}
        </Button>
      </div>

      <ErrorAlert className="mt-4">{error}</ErrorAlert>

      {/* The server's refusal, verbatim. It names the reason — another
          lockbox, no home, already reversed — in the same words the
          hold reasons use. */}
      {attachError && (
        <ErrorAlert className="mt-4" title="Couldn't attach">
          {attachError}
        </ErrorAlert>
      )}

      {/*
        Three empty states meaning three different things. "We have not
        looked" must never read as "nothing bounced" — they are opposite
        facts and only one of them is reassuring.
      */}
      {!loading && !error && neverPulled && (
        <Text className="mt-4">
          This connection has not been read for chargebacks yet. That is not the
          same as nothing having bounced.
        </Text>
      )}

      {!loading && !error && !neverPulled && data?.total === 0 && (
        <Text className="mt-4">
          {openOnly
            ? `Nothing needs attention. Read through ${formatDate(
                data.synced_through
              )}.`
            : `No payments have been returned. Read through ${formatDate(
                data.synced_through
              )}.`}
        </Text>
      )}

      {!loading && !error && data != null && data.total > 0 && (
        <>
          <Subheading className="mt-6">
            {data.total} chargeback{data.total === 1 ? '' : 's'} · read through{' '}
            {formatDate(data.synced_through)}
          </Subheading>
          <Table className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeader>Date</TableHeader>
                <TableHeader>Amount</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Cheque</TableHeader>
                <TableHeader>Bank&rsquo;s reason</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.chargebacks.map((c) => {
                const status = statusInfo(c);
                const isAttaching = attaching === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <TableRow>
                      <TableCell>{formatDate(c.chargeback_date)}</TableCell>
                      <TableCell>
                        {c.amount ? (
                          `$${c.amount}`
                        ) : (
                          // Unreadable is a defect, not an unusual
                          // chargeback, and it should look wrong.
                          <span className="text-red-600 dark:text-red-400">
                            {c.amount_raw ?? '—'}
                          </span>
                        )}
                        {/* What COLUMBIA charged the association. Not
                            the HOA's returned-payment fee, which is a
                            policy number charged to the homeowner from
                            the home's ledger — conflating them would
                            charge a homeowner the bank's cost. */}
                        {c.fee && c.fee !== '0' && c.fee !== '0.00' && (
                          <Text className="text-xs">bank fee ${c.fee}</Text>
                        )}
                      </TableCell>
                      <TableCell>{c.chargeback_type ?? '—'}</TableCell>
                      <TableCell>{c.check_number ?? '—'}</TableCell>
                      <TableCell>
                        {/* Free text from the bank, and inconsistent
                            across sources — shown, never acted on. The
                            structured code sits beside it where there
                            is one, which is never for a cheque. */}
                        {c.return_reason ?? '—'}
                        {c.return_reason_code && (
                          <span className="ml-1 text-zinc-500">
                            ({c.return_reason_code})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge color={status.tone}>{status.label}</Badge>
                        {/* Present only on a row that came from the
                            mock. A reversal is real money off a
                            homeowner's ledger, so "did this come from
                            Columbia" has to be answerable from the
                            screen. */}
                        {c.mock_source_url && (
                          <Badge color="purple" className="ml-1">
                            Fixture
                          </Badge>
                        )}
                        {documentsSaved(c) && (
                          <Badge color="zinc" className="ml-1">
                            Documents saved
                          </Badge>
                        )}
                        {/* A person's decision. The matcher leaves it
                            alone; the next run reverses it. */}
                        {c.attached_by && c.action_status !== 'reversed' && (
                          <Badge color="sky" className="ml-1">
                            Attached by hand
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button plain onClick={() => setViewing(c)}>
                            Documents
                          </Button>
                          {canAttach(c) && (
                            <Button
                              outline
                              onClick={() =>
                                isAttaching
                                  ? setAttaching(null)
                                  : void openAttach(c.id)
                              }
                            >
                              {isAttaching ? 'Cancel' : 'Attach'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {c.hold_reason && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-zinc-500">
                          {HOLD_HELP[c.hold_reason] ?? c.hold_reason}
                          {/* The one place the payer behind an ACH
                              return can be read. A bare link would
                              fail from a laptop — the portal is
                              IP-whitelisted like the API — so the
                              pointer says how to get there. */}
                          {isAch(c) && c.action_status !== 'reversed' && (
                            <span className="ml-1">
                              Who paid is on the lockbox portal’s{' '}
                              <em>eKlik Reversal Details</em> report:{' '}
                              <Link
                                href={LOCKBOX_PORTAL_URL}
                                target="_blank"
                                rel="noreferrer"
                              >
                                portal.columbiabank.io
                              </Link>
                              . The portal only answers from the bank
                              egress; open it with{' '}
                              <code>scripts/columbia-browser.sh</code>.
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    )}

                    {/* A reversed row: the fee is a separate decision,
                        made on the home's ledger, not here. */}
                    {c.action_status === 'reversed' && c.hoa_id && c.home_id && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-zinc-500">
                          Reversed {formatDate(c.reversed_at)}
                          {c.reversed_by === 'system' ? ' by the daily run' : ''}
                          . No fee was charged. To charge the association’s
                          returned-payment fee, use <em>Charge the fee</em> on
                          the payment in{' '}
                          <Link href={`/hoa/${c.hoa_id}/home/${c.home_id}/billing`}>
                            the home’s billing
                          </Link>
                          .
                        </TableCell>
                      </TableRow>
                    )}

                    {isAttaching && (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Text className="mb-2">
                            Which payment did this take back? Only payments
                            that came through this connection’s lockbox and
                            are matched to a home are offered — the same rule
                            the attach enforces.
                          </Text>
                          <ErrorAlert className="mb-2">{candidatesError}</ErrorAlert>
                          {candidates == null && !candidatesError && (
                            <Text>Looking up payments…</Text>
                          )}
                          {candidates != null && candidates.length === 0 && (
                            <Text className="mb-2">
                              No payment on this lockbox is open to be
                              reversed
                              {claimedElsewhere > 0
                                ? ` (${claimedElsewhere} already named by another chargeback)`
                                : ''}
                              .
                            </Text>
                          )}
                          {candidates != null && candidates.length > 0 && (
                            <Table className="mb-2" dense>
                              <TableHead>
                                <TableRow>
                                  <TableHeader>Home</TableHeader>
                                  <TableHeader>Payer</TableHeader>
                                  <TableHeader>Cheque</TableHeader>
                                  <TableHeader>Amount</TableHeader>
                                  <TableHeader>Received</TableHeader>
                                  <TableHeader />
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {candidates.map((p) => (
                                  <TableRow key={p.payment_id}>
                                    <TableCell>{p.home_label ?? p.home_id}</TableCell>
                                    <TableCell>{p.payer_name ?? '—'}</TableCell>
                                    <TableCell>
                                      {p.check_number ?? '—'}
                                      {p.same_check_number && (
                                        <Badge color="lime" className="ml-1">
                                          same number
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      ${p.amount}
                                      {p.same_amount && (
                                        <Badge color="lime" className="ml-1">
                                          same amount
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell>{formatDate(p.received_date)}</TableCell>
                                    <TableCell>
                                      <Button
                                        outline
                                        disabled={busy}
                                        onClick={() => void attach(c.id, p.payment_id)}
                                      >
                                        {busy ? 'Attaching…' : 'This one'}
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                          {/* The old way, kept behind a click for the
                              case the list cannot know about. The server
                              applies the same checks either way. */}
                          {!showOtherId ? (
                            <Button plain onClick={() => setShowOtherId(true)}>
                              Enter a payment id instead
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Input
                                value={paymentId}
                                onChange={(e) => setPaymentId(e.target.value)}
                                placeholder="Payment id, e.g. pay_…"
                                aria-label="Payment id"
                              />
                              <Button
                                onClick={() => void attach(c.id, paymentId)}
                                disabled={busy || paymentId.trim() === ''}
                              >
                                {busy ? 'Attaching…' : 'Attach this id'}
                              </Button>
                            </div>
                          )}
                          {/* Said plainly, because the button does less
                              than people expect it to. */}
                          <Text className="mt-2">
                            Attaching records which payment this reverses. It
                            does not reverse it — the next run does that, with
                            the same checks every other chargeback goes through.
                          </Text>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}

      <ChargebackDocumentsDialog
        chargeback={viewing}
        onClose={() => setViewing(null)}
      />
    </div>
  );
};

export default ChargebackPanel;
